import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

function SOC2Badge() {
  const { t } = useTranslation()
  return (
    <svg viewBox="0 0 100 100" className="w-20 h-20 shrink-0" aria-hidden="true">
      <circle cx="50" cy="50" r="48" fill="#ffffff" />
      <circle cx="50" cy="50" r="48" fill="none" stroke="#1e3a8a" strokeWidth="1.5" />
      <circle cx="50" cy="50" r="42" fill="none" stroke="#1e3a8a" strokeWidth="0.8" />
      <path
        d="M50 14 L76 24 L76 52 Q76 74 50 86 Q24 74 24 52 L24 24 Z"
        fill="#1e3a8a"
      />
      <text x="50" y="44" textAnchor="middle" fontSize="16" fontWeight="800" fill="#ffffff" fontFamily="Arial, sans-serif">
        SOC 2
      </text>
      <text x="50" y="58" textAnchor="middle" fontSize="9" fontWeight="700" fill="#ffffff" letterSpacing="1.8" fontFamily="Arial, sans-serif">
        TYPE I
      </text>
      <path d="M42 66 L47 71 L58 60" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <text x="50" y="96" textAnchor="middle" fontSize="5" fontWeight="600" fill="#1e3a8a" letterSpacing="0.5" fontFamily="Arial, sans-serif">
        
        {t('auth.aicpaCertified')}
      </text>
    </svg>
  )
}

export default function AuthLayout() {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-linear-to-br/srgb from-brand-600 to-brand-800 p-12 flex-col justify-between">
        <div>
          <h1 className="text-4xl font-bold text-white">A.L.D.O</h1>
          <p className="text-brand-200 mt-2">{t('auth.tagline')}</p>
        </div>

        <div className="space-y-8">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold">{t('auth.feature.discovery')}</h3>
              <p className="text-brand-200 text-sm">{t('auth.feature.discoveryDesc')}</p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold">{t('auth.feature.insights')}</h3>
              <p className="text-brand-200 text-sm">{t('auth.feature.insightsDesc')}</p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold">{t('auth.feature.guardrails')}</h3>
              <p className="text-brand-200 text-sm">{t('auth.feature.guardrailsDesc')}</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-lg backdrop-blur-xs border border-white/20">
            <SOC2Badge />
            <div className="min-w-0">
              <h4 className="text-white font-semibold text-base leading-tight">
                
                {t('auth.soc2Certified')}
              </h4>
              <p className="text-brand-200 text-xs mt-1 leading-snug">
                
                {t('auth.soc2Desc')}
              </p>
            </div>
          </div>

          <p className="text-brand-300 text-sm">
            
            {t('auth.footerFeatures')}
          </p>
        </div>
      </div>

      {/* Right side - Auth Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Outlet />

          {/* SOC 2 badge visible on mobile/smaller screens (where left panel is hidden) */}
          <div className="lg:hidden mt-6 flex items-center justify-center space-x-2 text-xs text-muted-foreground">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <span>{t('auth.soc2Certified')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
